import { Button } from "antd";
import { ReactElement, ReactNode } from 'react';
import Icon, { ICON_NAME } from "../../components/Icon";
import LoadingBox from "../../components/LoadingBox";
import Action from "../../pages/LocationPage/Action";
import { OP_STATUS, propContainer } from "./constants";
import { checkZero, formatNumber } from "./global";
import { IFactory, IOperation, IPois, IRegion, ISite } from "./interface";
import { ACTION } from "./permission";

export const Symbol = {
    m3: <>m³</>
}

export enum TemplateCode {
    T1 = 'convert-string-to-html',
    T2 = 'response-send-operation-to-vessel',
    T3 = 'response-send-request-to-vessel',
    T4 = 'warning-related-operation'
}

export const template = {
    [TemplateCode.T1]: (data: string | ReactElement): ReactElement => typeof data !== 'string'
        ? data
        : <div dangerouslySetInnerHTML={{ __html: data }} />,

    [TemplateCode.T2]: (content: string, newOp: any = [], oldOp: any = []) => <div>
        <div>{content}</div>
        <div>{newOp.length > 0 ? `New: ${newOp.join(', ')}.\n` : ''}</div>
        <div>{oldOp.length > 0 ? `Update: ${oldOp.join(', ')}.` : ''}</div>
    </div>,

    [TemplateCode.T3]: (newRequest: string[] = [], existRequest: string[] = []) => <>
        {newRequest.length > 0 &&
            <p className='mb-1'>Request has been sent to {newRequest.join(', ')} successfully</p>}
        {existRequest.length > 0 && <p className='mb-1'>
            {`${existRequest.join(', ')} ${existRequest.length === 1 ? 'has' : 'have'} been added to Plan operation page already`}
        </p>}
    </>,

    [TemplateCode.T4]: (props: ({ [id: string]: IOperation[] })[]) => <>
        {props.map((item, i) => {
            const { ids_true = [], ids_false = [], related = [] } = item;
            const updated: string[] = [];
            [...ids_true, ...ids_false].forEach(item => {
                if (item.operation.status !== OP_STATUS.NEW)
                    updated.push(`#${item.operation.operation_code}`)
            })
            return <div key={i}>
                Updated operation <span className="font-[600]">{updated.join(", ")}</span>: - Related ones: <span className="font-[600]">{related.map(item => `#${item.operation.operation_code} (on ${item.vessel.name})`).join(', ')}</span>
            </div>
        })}
    </>
}

export const propsSelect = {
    ...propContainer,
    notFoundContent: <LoadingBox />
}

export const ColumnTemplates = {
    Name: {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
    WorkTime: {
        title: 'Work time',
        dataIndex: 'work_time',
        key: 'work_time',
        render: (work_time: any) => {
            let hours = Math.floor(work_time);
            let min = Math.floor(work_time - hours) * 60;
            hours = hours % 12;
            hours = hours ? hours : 12
            return <span>{`${hours}:${min > 0 ? checkZero(min) : '00'} ${hours < 12 ? 'AM' : 'PM'}`}</span>
        },
    },
    Phone: {
        title: 'Phone number',
        dataIndex: 'phone',
        key: 'phone',
    },
    Position: {
        title: 'Lat, Long',
        dataIndex: 'position',
        key: 'position',
        render: (data: any) => <span>{[data.latitude, data.longitude].join(', ')}</span>,
    },
    Regions: (regions: IRegion[]) => ({
        title: 'Region',
        dataIndex: 'region_id',
        key: 'region_id',
        render: (regionId: string) => {
            const { name = '' } = regions.find(item => item.id === regionId) || {};
            return name;
        }
    }),
    Render: <T extends unknown>(title: string, render: (data: T) => string | ReactNode) => ({
        title,
        key: title,
        render
    }),
    Info: (title: string, key: string) => ({
        title,
        dataIndex: key,
        key: key,
        sorter: (a: any, b: any) => a[key] - b[key],
    }),
    SorterNoRender: (title: string, keySort: string) => ({
        title,
        key: title,
        sorter: (a: any, b: any) => a[keySort] - b[keySort],

    }),
    Number: (title: string, key: string, suffix: string = "") => ({
        title,
        key: title,
        sorter: (a: any, b: any) => a[key] - b[key],
        render: (data: any) => `${formatNumber(data[key])} ${suffix}`,
    }),
    NumberRender: <T extends unknown>(title: string, key: string, render: (data: T) => string | number, suffix?: string) => ({
        title,
        key: title,
        sorter: (a: any, b: any) => a[key] - b[key],
        render: (data: T) => `${formatNumber(render(data))} ${suffix}`,
    }),
    Detail: <T extends unknown>(title: string, label: (data: T) => string | ReactNode, onClick: (data: T) => void) => ({
        title,
        key: title,
        render: (data: T) => <div
            className='link'
            onClick={() => onClick(data)}
        >
            {label(data)}
        </div>,
    }),
    Edit: (permission: any, onClick: Function) => ({
        className: 'column-action',
        width: '48px',
        render: (data: any) => permission[ACTION.LOCATION.UPDATE] && <Button
            className='bt-ghost w-[32px] m-auto'
            data-icon='true'
            onClick={() => onClick(data)}
        >
            <Icon icon={ICON_NAME.EDIT} size="normal" />
        </Button>,
    }),
    Menu: function <S extends (ISite | IFactory | IPois)>(onEdit: (value: S) => void) {
        return {
            key: 'action',
            width: 80,
            title: ' ',
            className: 'column-action',
            render: (data: S) => <Action<S> data={data} onEdit={onEdit} />
        }
    }
}
